import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, User } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";

const PIPELINE = ["inviata", "in_revisione", "in_sviluppo", "pubblicata"];

export default function Admin() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [ideas, setIdeas] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = () => api.get("/admin/ideas").then(({ data }) => setIdeas(data)).catch(() => {});
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const setStatus = async (idea, next) => {
    if (next === idea.status) return;
    setBusyId(idea.id);
    try {
      const { data } = await api.patch(`/admin/ideas/${idea.id}/status`, { status: next, note: notes[idea.id] || null });
      toast.success(`${t.toastStatus}: ${t.statuses[next]}. ${data.email_sent ? t.emailSent : t.emailFail}`);
      setNotes((n) => ({ ...n, [idea.id]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#080A0C] text-gray-100 terminal-grid">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="admin-header">
          <p className="font-mono text-xs tracking-[0.3em] text-[#00FF66] mb-3">{t.adminTag}</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-white">{t.adminTitle}</h1>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">{t.adminSub}</p>
        </motion.div>

        <div className="mt-10 space-y-4" data-testid="admin-ideas-list">
          {ideas.length === 0 && (
            <div className="glass-card rounded-2xl p-10 text-center font-mono text-sm text-gray-500" data-testid="admin-empty-state">{t.noIdeas}</div>
          )}
          {ideas.map((idea, i) => (
            <motion.div key={idea.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`admin-idea-${idea.id}`}
              className="glass-card glow-border rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display font-semibold text-white">{idea.title}</h3>
                  <span data-testid={`admin-status-${idea.id}`} className={`font-mono text-[10px] tracking-widest px-3 py-1 rounded-full border uppercase badge-${idea.status}`}>
                    {t.statuses[idea.status]}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mt-2 line-clamp-2">{idea.description}</p>
                <p className="flex items-center gap-3 font-mono text-[10px] text-gray-500 mt-3 flex-wrap">
                  <span className="flex items-center gap-1"><User size={10} /> {idea.user_name}</span>
                  <span className="flex items-center gap-1"><Mail size={10} /> {idea.user_email}</span>
                  <span>{new Date(idea.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</span>
                </p>
                <input data-testid={`admin-note-input-${idea.id}`} className="gc-input mt-4 !py-2.5 text-xs" maxLength={500}
                  placeholder={t.phNote} value={notes[idea.id] || ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [idea.id]: e.target.value }))} />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <select data-testid={`admin-status-select-${idea.id}`} value={idea.status} disabled={busyId === idea.id}
                  onChange={(e) => setStatus(idea, e.target.value)}
                  className="gc-input !w-auto !py-2.5 cursor-pointer disabled:opacity-50">
                  {PIPELINE.map((s) => (
                    <option key={s} value={s}>{t.statuses[s]}</option>
                  ))}
                </select>
                {idea.status !== "pubblicata" && (
                  <button data-testid={`admin-advance-${idea.id}`} onClick={() => setStatus(idea, PIPELINE[PIPELINE.indexOf(idea.status) + 1])} disabled={busyId === idea.id}
                    className="btn-neon px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                    {busyId === idea.id ? "..." : t.advance} <ArrowRight size={13} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
